import * as React from "react";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Box, Typography } from "@mui/material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Lancamentos from "./Lancamentos";
import LancamentoPendentes from "./LancamentoPendentes";
import LancamentosCancelados from "./LancamentosCancelados";
import Evolucao from "./Evolucao";
import { IRelPaymentsPeriod } from "interfaces/reports";
import { TypeStatusInscricao } from "interfaces/inscricoes";
import { EXTRATO_ACCENT } from "../extratos/components/extrato.theme";

interface Props {
  data: IRelPaymentsPeriod[];
  totals: Partial<{
    [k in TypeStatusInscricao]: { qtd: number; total: number };
  }>;
  loading?: boolean;
}

function TabLabel({ icon, text, count }: { icon: React.ReactNode; text: string; count?: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {icon}
      <Typography variant="button" sx={{ display: { xs: "none", sm: "block" }, textTransform: "none" }}>
        {text}
        {count != null && count > 0 ? ` (${count})` : ""}
      </Typography>
    </Box>
  );
}

export default function TabsVisaoGeral({ data, totals, loading }: Props) {
  const [value, setValue] = React.useState("1");

  const pendentes = data.filter(
    (item) => item.inscricaoEvento.statusInscricao.tipo === "Pendente"
  );
  const pagos = data.filter((item) => item.inscricaoEvento.statusInscricao.tipo === "Pago");
  const cancelados = data.filter(
    (item) => item.inscricaoEvento.statusInscricao.tipo === "Cancelado"
  );

  const emptyMessage =
    value === "1"
      ? "Nenhum lançamento confirmado no período."
      : value === "2"
        ? "Nenhuma inscrição pendente no período."
        : value === "3"
          ? "Nenhuma inscrição cancelada no período."
          : null;

  return (
    <MDBox
      sx={{
        width: "100%",
        typography: "body1",
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "grey.200", px: { xs: 1, sm: 2 }, pt: 1 }}>
          <TabList
            onChange={(_event, newValue) => setValue(newValue)}
            aria-label="Extrato inscrições"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                minHeight: 44,
                textTransform: "none",
                fontWeight: 500,
                color: "text.secondary",
              },
              "& .MuiTab-root.Mui-selected": {
                color: EXTRATO_ACCENT,
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: EXTRATO_ACCENT,
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab
              label={
                <TabLabel
                  icon={<PaidOutlinedIcon fontSize="small" />}
                  text="Confirmadas"
                  count={pagos.length}
                />
              }
              value="1"
            />
            <Tab
              label={
                <TabLabel
                  icon={<HourglassTopOutlinedIcon fontSize="small" />}
                  text="Pendentes"
                  count={pendentes.length}
                />
              }
              value="2"
            />
            <Tab
              label={
                <TabLabel
                  icon={<CancelOutlinedIcon fontSize="small" />}
                  text="Canceladas"
                  count={cancelados.length}
                />
              }
              value="3"
            />
            <Tab
              label={
                <TabLabel icon={<TrendingUpOutlinedIcon fontSize="small" />} text="Evolução" />
              }
              value="4"
            />
          </TabList>
        </Box>

        {!loading && data.length === 0 ? (
          <MDBox py={6} textAlign="center">
            <MDTypography variant="body2" color="text">
              Nenhum registro encontrado para o período selecionado.
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <TabPanel sx={{ p: { xs: 1.5, sm: 2 } }} value="1">
              {!loading && pagos.length === 0 ? (
                <MDTypography variant="body2" color="text" py={4} textAlign="center">
                  {emptyMessage}
                </MDTypography>
              ) : (
                <Lancamentos pagos={pagos} totals={totals} />
              )}
            </TabPanel>
            <TabPanel sx={{ p: { xs: 1.5, sm: 2 } }} value="2">
              {!loading && pendentes.length === 0 ? (
                <MDTypography variant="body2" color="text" py={4} textAlign="center">
                  {emptyMessage}
                </MDTypography>
              ) : (
                <LancamentoPendentes pendentes={pendentes} totals={totals} />
              )}
            </TabPanel>
            <TabPanel sx={{ p: { xs: 1.5, sm: 2 } }} value="3">
              {!loading && cancelados.length === 0 ? (
                <MDTypography variant="body2" color="text" py={4} textAlign="center">
                  {emptyMessage}
                </MDTypography>
              ) : (
                <LancamentosCancelados cancelados={cancelados} totals={totals} />
              )}
            </TabPanel>
            <TabPanel sx={{ p: { xs: 1.5, sm: 2 } }} value="4">
              <Evolucao />
            </TabPanel>
          </>
        )}
      </TabContext>
    </MDBox>
  );
}
